import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Wifi, Tv, Router, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';

const iconMap = {
  wifi: Wifi,
  tv: Tv,
  router: Router
};

export const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-red-600">Services</span>
            </h1>
            <p className="text-xl text-gray-600">
              Comprehensive connectivity solutions designed for modern living
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="space-y-20">
            {mockData.services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={service.id}
                  className={`flex flex-col ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } gap-12 items-center`}
                >
                  {/* Icon Side */}
                  <div className="flex-1">
                    <div className="w-full max-w-md mx-auto bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-12 flex items-center justify-center">
                      <Icon size={120} className="text-red-600" />
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="flex-1">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">{service.title}</h2>
                    <p className="text-xl text-gray-600 mb-8">{service.description}</p>

                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={16} className="text-red-600" />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/plans">
                      <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                        View Plans
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose i5World?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience the difference with our premium services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="text-3xl font-bold text-red-600 mb-3">1 Gbps</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Maximum Speed</h3>
                <p className="text-gray-600">
                  Experience lightning-fast internet speeds up to 1 Gbps for seamless streaming,
                  gaming, and work.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="text-3xl font-bold text-red-600 mb-3">500+</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">TV Channels</h3>
                <p className="text-gray-600">
                  Access over 500 live TV channels and premium OTT apps for unlimited entertainment.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="text-3xl font-bold text-red-600 mb-3">24/7</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Support</h3>
                <p className="text-gray-600">
                  Round-the-clock customer support ready to help you with any questions or issues.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-red-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Experience the Best?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get started with i5World today and enjoy unparalleled connectivity
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
